import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../../utils/api';
import FeatureTip from '../../components/FeatureTip';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Building2, Clock, Users, Archive } from 'lucide-react';
import { ProjectCard } from './project/ProjectCard';
import { projectStatus } from '../../lib/projectVisuals';

const emptyFormData = {
  name: '',
  customer: '',
  customer_email: '',
  location: '',
  start_date: '',
  end_date: '',
  description: '',
  status: 'active',
  budget: '',
  billing_profile_id: '',
  project_hourly_rate: '',
  employee_rates: {},   // { employee_id: rate }
};

const Projects = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState(emptyFormData);

  useEffect(() => {
    fetchProjects();
    // Ladda fakturabolag och personal parallellt för projekt-skapande
    api.getProjectSummaries().then(r => setSummaries(r.data?.summaries || {})).catch(() => {});
    api.listBillingProfiles().then(r => setBillingProfiles(r.data || [])).catch(() => {});
    api.getEmployees().then(r => setEmployees(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, statusFilter, showArchived]);

  const fetchProjects = async () => {
    try {
      const response = await api.getProjects({ include_archived: true });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Kunde inte hämta projekt');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = projects;

    // Filtrera arkiverade — visas bara om toggle aktiv
    if (showArchived) {
      filtered = filtered.filter(p => p.archived);
    } else {
      filtered = filtered.filter(p => !p.archived);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => projectStatus(p).label === statusFilter);
    }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.customer && p.customer.toLowerCase().includes(q)) ||
        (p.location && p.location.toLowerCase().includes(q))
      );
    }

    setFilteredProjects(filtered);
  };

  const toggleArchive = async (project, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      await api.archiveProject(project.id, !project.archived);
      toast.success(project.archived ? 'Projekt återställt' : 'Projekt arkiverat');
      fetchProjects();
    } catch {
      toast.error('Kunde inte arkivera projektet');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Rensa tomma strängar → null så Pydantic inte klagar på "input too short"
      // för valfria fält som datum, budget, etc.
      const scrub = (v) => (v === '' || v == null ? null : v);
      const payload = {
        name: formData.name,
        customer: scrub(formData.customer),
        customer_email: scrub(formData.customer_email),
        budget: formData.budget === '' || formData.budget == null ? null : parseFloat(formData.budget),
        location: scrub(formData.location),
        start_date: scrub(formData.start_date),
        end_date: scrub(formData.end_date),
        description: scrub(formData.description),
        billing_profile_id: scrub(formData.billing_profile_id),
        project_hourly_rate: formData.project_hourly_rate ? parseFloat(formData.project_hourly_rate) : null,
        employee_rates: Object.fromEntries(
          Object.entries(formData.employee_rates || {})
            .filter(([, v]) => v !== '' && v != null)
            .map(([k, v]) => [k, parseFloat(v)])
        ),
      };
      const res = await api.createProject(payload);
      const newProjectId = res.data?.id;
      toast.success('Projekt skapat! Guidar dig genom nästa steg...');
      setDialogOpen(false);
      setFormData(emptyFormData);
      setShowAdvanced(false);
      if (newProjectId) {
        // Skicka admin direkt till nya projektet så wizarden visas för första steget (Geofence)
        navigate(`/dashboard/projects/${newProjectId}?tab=material-plan`);
      } else {
        fetchProjects();
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Kunde inte skapa projekt'));
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#F26B1F] border-t-transparent op-round rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      <FeatureTip tipId="projects" title="Projekt — din arbetsbas" body="Skapa ett projekt för varje uppdrag. Inne i projektet hittar du budget, tidrapporter, ÄTA, material och geofence. AI-knapparna på dashboarden tar dig hit direkt." />
      <div className="flex flex-col gap-5">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">Projekt</h1>
            <p className="text-sm text-stone-500 mt-1">{filteredProjects.length} {showArchived ? 'arkiverade' : 'aktiva'} projekt · från ritning till färdig kalkyl</p>
          </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-project-btn" className="op-btn-primary">
              <Plus size={18} />
              <span className="hidden sm:inline">Nytt projekt</span>
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Skapa nytt projekt</DialogTitle>
            </DialogHeader>
            <div className="bg-[hsl(16,100%,55%)]/8 border border-[hsl(16,100%,55%)]/30 rounded-lg p-3 text-xs mb-2" data-testid="wizard-info">
              <p className="font-semibold text-foreground mb-1">Så fungerar det nya flödet:</p>
              <p className="text-muted-foreground leading-relaxed">
                Fyll bara i projektnamnet nu. Resten räknar AI fram åt dig i rätt ordning:
                <strong className="text-foreground"> Material → Arbete → Tid → Offert</strong>.
                Budget och deadline sätts automatiskt när offerten godkänns.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Projektnamn *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="project-name-input"
                  placeholder="t.ex. F-6 skola Mörbylund"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Kund (valfritt — kan fyllas i senare)</Label>
                <Input
                  id="customer"
                  value={formData.customer}
                  onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
                  data-testid="project-customer-input"
                  placeholder="Företagsnamn eller personnamn"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Kort beskrivning (valfritt)</Label>
                <textarea
                  id="description"
                  className="w-full min-h-[60px] px-3 py-2 border border-input rounded text-sm bg-background"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="project-description-input"
                  placeholder="En rad om projektet. Mer beskrivning läggs till när du kör materialberäkningen."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" data-testid="create-project-submit-btn" className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white">Skapa &amp; öppna wizard</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 max-w-lg">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" size={17} />
            <input placeholder="Sök projekt…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="search-projects-input"
              className="w-full h-11 pl-10 pr-3 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#F26B1F]" />
          </div>
          <div className="flex gap-1.5 overflow-x-auto op-noscroll items-center" data-testid="status-filter-chips">
            {[['all', 'Alla'], ['Pågående', 'Pågående'], ['Planering', 'Planering'], ['Klar', 'Klart']].map(([v, l]) => (
              <button key={v} className="op-chip h-9" data-active={statusFilter === v} onClick={() => setStatusFilter(v)} data-testid={`status-chip-${v}`}>{l}</button>
            ))}
            <button className="op-chip h-9" data-active={showArchived} onClick={() => setShowArchived(!showArchived)} data-testid="toggle-archived-projects">
              <Archive size={13} />{showArchived ? 'Visar arkiverade' : 'Arkiverade'}
            </button>
          </div>
        </div>
      </div>

      {filteredProjects.length === 0 ? (
        <div className="op-card p-12 text-center" data-testid="no-projects-found">
          <p className="font-semibold">Inga projekt hittades</p>
          <p className="text-sm text-stone-500 mt-1">Skapa ett projekt och låt AI räkna fram material, arbete och tid.</p>
          <button onClick={() => setDialogOpen(true)} className="op-btn-primary mt-5" data-testid="empty-new-project-btn"><Plus size={16} />Nytt projekt</button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="projects-grid">
          {filteredProjects.map((project, i) => (
            <ProjectCard key={project.id} project={project} summary={summaries[project.id]} onArchive={toggleArchive} index={i} />
          ))}
        </div>
      )}
      <button onClick={() => setDialogOpen(true)} className="lg:hidden fixed right-5 bottom-24 z-30 w-14 h-14 rounded-full bg-[#F26B1F] text-white shadow-[0_14px_30px_-10px_rgba(242,107,31,1)] flex items-center justify-center" data-testid="projects-fab"><Plus size={24} /></button>
    </div>
  );
};

export default Projects;
